import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/lib/errors';
import { Field } from '@/components/ui/Field';
import { BrandMark } from '@/components/ui/BrandMark';
import { tokenStore } from '@/lib/tokenStore';

const LoginSchema = z.object({
  username: z.string().min(2, 'Enter your username').max(200),
  pin: z.string().regex(/^\d{6}$/, 'PIN must be exactly 6 digits')
});

type LoginFormValues = z.infer<typeof LoginSchema>;

interface LocationState {
  from?: string;
}

function clearAllClientState() {
  try {
    tokenStore.clear();
    localStorage.clear();
    sessionStorage.clear();
    // Wipe every cookie visible to JS for the current document.
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      if (!name) return;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  } catch {
    // best-effort cleanup; ignore failures
  }
}

/**
 * V4.9.5+ — Backdrop product photos that animate behind the login card.
 *
 * V4.9.6 BUGFIX (server-side caught): images were previously hot-linked
 * from www.backdropsource.com/cdn/shop/files/* directly. In production
 * the Shopify CDN refused to serve them cross-origin (either via CORP
 * / Referrer-Policy / hot-link protection or via the deployed CSP),
 * which left the login page with bare tile outlines and no pictures
 * inside them.
 *
 * Fix: the images are now bundled inside the app at
 * /frontend/public/login-bg/*.{png,webp}. Vite copies them verbatim to
 * `dist/login-bg/` so they're served by the same origin as the rest of
 * the app — no CDN, no CSP issue, no hot-link block. They also cache
 * forever via the static-asset pipeline.
 *
 * To swap images: drop new files into /frontend/public/login-bg/ and
 * update the paths below. The columns auto-fit any number of tiles.
 */
const BACKDROP_IMAGE_COLUMNS: string[][] = [
  // Column 1 — SEG / Pop-up displays
  [
    '/login-bg/01-seg.png',
    '/login-bg/02-popup.webp',
    '/login-bg/03-polyester.webp'
  ],
  // Column 2 — Modular exhibition kits
  [
    '/login-bg/04-exhibition.png',
    '/login-bg/05-20x20.png',
    '/login-bg/06-shell-scheme.png'
  ],
  // Column 3 — Canopy tents + photo booths
  [
    '/login-bg/07-canopy.webp',
    '/login-bg/08-curved.webp',
    '/login-bg/09-step-repeat.png'
  ],
  // Column 4 — Display counters + accessories
  [
    '/login-bg/10-table.webp',
    '/login-bg/11-counter.png',
    '/login-bg/12-banner.png'
  ]
];

export function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as LocationState | null)?.from;
  const redirectTo = fromState ?? '/dashboard';
  const [apiError, setApiError] = useState<string | null>(null);

  // Auto-clear any stale storage the moment the login page mounts. This
  // protects against leftover tokens or cookies from a previous backend.
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllClientState();
    }
  }, [isAuthenticated]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { username: '', pin: '' }
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const onSubmit = handleSubmit(async (values) => {
    setApiError(null);
    try {
      await login(values);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[login] failed', error);
      if (error instanceof ApiError) {
        const detail = error.problem?.detail ?? error.message;
        const field = error.problem?.errors
          ? Object.entries(error.problem.errors)
              .flatMap(([k, v]) => v.map((m) => `${k}: ${m}`))
              .join('; ')
          : '';
        const suffix = error.status ? ` [HTTP ${error.status}]` : '';
        setApiError(`${detail}${field ? ` — ${field}` : ''}${suffix}`);
      } else if (error instanceof Error) {
        setApiError(error.message);
      } else {
        setApiError('Login failed');
      }
    }
  });

  const handleFullReset = () => {
    clearAllClientState();
    setApiError(null);
    window.location.reload();
  };

  return (
    <div className="auth-shell">
      {/* V4.9.5+ — Parallax columns of backdrop product photos. Each
          column scrolls at a different speed (see .auth-bg-column-{n} in
          global.css) so the background feels cinematic. Images duplicate
          inside the column so the upward-scroll loops seamlessly. */}
      <div className="auth-bg" aria-hidden="true">
        {BACKDROP_IMAGE_COLUMNS.map((urls, colIdx) => (
          <div key={colIdx} className={`auth-bg-column auth-bg-column-${colIdx + 1}`}>
            {/* Duplicate the list so the CSS keyframes can translate the
                column upward by exactly 50% and loop cleanly without a
                visible jump. */}
            {[...urls, ...urls].map((url, i) => (
              <div key={`${colIdx}-${i}`} className="auth-bg-tile">
                {/* V4.9.6 — images are bundled in /public/login-bg so eager
                    load is fine; saves a layout shift on first paint vs lazy. */}
                <img src={url} alt="" loading="eager" decoding="async" />
              </div>
            ))}
          </div>
        ))}
        {/* Dark overlay sits on top of the column grid so the login card
            reads cleanly. Gradient is darker at the centre (behind the
            card) and slightly lighter at the edges so the product photos
            peek through the corners. */}
        <div className="auth-bg-overlay" />
      </div>

      <div className="auth-card auth-card-glass">
        <div className="brand">
          <BrandMark size={48} />
          <div>
            <h1 className="brand-title">Ticket Management</h1>
            <div className="subtitle">Software for Work</div>
          </div>
        </div>

        <form onSubmit={onSubmit} noValidate>
          <Field
            label="Username"
            htmlFor="username"
            required
            error={errors.username?.message ?? null}
          >
            <input
              id="username"
              autoFocus
              autoComplete="username"
              className="form-input"
              placeholder="Enter your username"
              {...register('username')}
            />
          </Field>
          <Field label="PIN" htmlFor="pin" required error={errors.pin?.message ?? null}>
            <input
              id="pin"
              type="password"
              autoComplete="current-password"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              className="form-input"
              placeholder="6-digit PIN"
              {...register('pin')}
            />
          </Field>
          {apiError ? (
            <div className="form-error mb-3" role="alert">
              {apiError}
            </div>
          ) : null}
          <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
            {isSubmitting ? <span className="spinner" aria-hidden="true" /> : null}
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        <p className="text-small text-muted mt-4">
          Access is by invitation only. Contact your administrator if you need a username or PIN reset.
        </p>
        <button
          type="button"
          className="btn btn-ghost btn-sm w-100 mt-2"
          onClick={handleFullReset}
        >
          Reset login state
        </button>
      </div>
    </div>
  );
}
